import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { cn } from '../../../utils/cn';

const ReportManagementPanel = ({ userId }) => {
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportData, setReportData] = useState({
    reportedUserId: '',
    category: '',
    description: '',
    evidence: []
  });
  const [submitting, setSubmitting] = useState(false);

  const reportCategories = [
    { value: 'inappropriate-behavior', label: 'Inappropriate Behavior' },
    { value: 'fake-profile', label: 'Fake Profile' },
    { value: 'harassment', label: 'Harassment' },
    { value: 'safety-concern', label: 'Safety Concern' },
    { value: 'spam', label: 'Spam' },
    { value: 'underage', label: 'Underage User' },
    { value: 'other', label: 'Other' }
  ];

  const myReports = [
    {
      id: 1,
      reportedUser: 'User #4523',
      category: 'Inappropriate Behavior',
      status: 'under-review',
      date: '2026-01-25',
      resolution: null
    },
    {
      id: 2,
      reportedUser: 'User #8721',
      category: 'Fake Profile',
      status: 'resolved',
      date: '2026-01-20',
      resolution: 'Account suspended'
    }
  ];

  const handleSubmitReport = async (e) => {
    e?.preventDefault();
    setSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      alert('Report submitted successfully. Our team will review it within 24 hours.');
      setShowReportForm(false);
      setReportData({
        reportedUserId: '',
        category: '',
        description: '',
        evidence: []
      });
    } catch (error) {
      console.error('Report submission error:', error);
      alert('Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'under-review': { label: 'Under Review', color: 'bg-warning/20 text-warning', icon: 'Clock' },
      'resolved': { label: 'Resolved', color: 'bg-success/20 text-success', icon: 'CheckCircle' },
      'dismissed': { label: 'Dismissed', color: 'bg-muted text-muted-foreground', icon: 'XCircle' }
    };

    const config = statusConfig?.[status] || statusConfig?.['under-review'];

    return (
      <div className={cn("inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium", config?.color)}>
        <Icon name={config?.icon} size={12} />
        <span>{config?.label}</span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-1">Report Management</h2>
          <p className="text-sm text-muted-foreground">File reports and track their status</p>
        </div>
        <Button
          onClick={() => setShowReportForm(!showReportForm)}
          iconName="Flag"
          variant={showReportForm ? "outline" : "default"}
        >
          {showReportForm ? 'Cancel' : 'New Report'}
        </Button>
      </div>

      {/* Report Form */}
      {showReportForm && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">File a Report</h3>
          <form onSubmit={handleSubmitReport} className="space-y-4">
            <Input
              label="Reported User ID"
              placeholder="Enter user ID or profile link"
              value={reportData?.reportedUserId}
              onChange={(e) => setReportData({ ...reportData, reportedUserId: e?.target?.value })}
              required
              description="You can find the user ID in their profile URL"
            />

            <Select
              label="Report Category"
              placeholder="Select a category"
              options={reportCategories}
              value={reportData?.category}
              onChange={(value) => setReportData({ ...reportData, category: value })}
              required
            />

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Description <span className="text-destructive">*</span>
              </label>
              <textarea
                className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Please provide detailed information about the incident..."
                value={reportData?.description}
                onChange={(e) => setReportData({ ...reportData, description: e?.target?.value })}
                required
              />
              <p className="text-xs text-muted-foreground">Be as specific as possible. Include dates, times, and any relevant context.</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Evidence (Optional)</label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer">
                <Icon name="Upload" size={32} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-foreground mb-1">Upload screenshots or other evidence</p>
                <p className="text-xs text-muted-foreground">PNG, JPG up to 10MB</p>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" loading={submitting} fullWidth>
                Submit Report
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowReportForm(false)} fullWidth>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* My Reports */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">My Reports</h3>
        
        {myReports?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Flag" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No reports filed yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {myReports?.map((report) => (
              <div key={report?.id} className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">{report?.category}</h4>
                    <p className="text-sm text-muted-foreground">Reported: {report?.reportedUser}</p>
                  </div>
                  {getStatusBadge(report?.status)}
                </div>
                
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Icon name="Calendar" size={14} />
                    <span>{new Date(report?.date)?.toLocaleDateString()}</span>
                  </div>
                  {report?.resolution && (
                    <div className="flex items-center gap-1">
                      <Icon name="Info" size={14} />
                      <span>{report?.resolution}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Safety Notice */}
      <div className="bg-error/5 border border-error/20 rounded-lg p-4">
        <div className="flex gap-3">
          <Icon name="AlertTriangle" size={20} className="text-error mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground mb-1">Emergency Situations</p>
            <p className="text-sm text-muted-foreground">
              If you are in immediate danger, please contact local emergency services. Our report system is for platform safety concerns only.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportManagementPanel;